// ============================================================
//  src/cabrillo-logs.js — Acceso a los logs Cabrillo que el
//  usuario ya tiene guardados en Contest Manager.
//
//  Módulo intencionalmente desacoplado del wrapper del Analizador
//  Cabrillo: además de alimentar la selección/carga automática,
//  sirve de base para una futura herramienta de comparación entre
//  concursos (misma fuente de datos, sin duplicarla).
//
//  No duplica el Cabrillo en ningún otro almacenamiento: siempre
//  se resuelve "al vuelo" desde el Drive (propio o administrado)
//  donde ya vive.
// ============================================================

import { collection, query, where, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { db, functions } from './firebase.js';
import { obtenerArchivosEdicion } from './firestore.js';
import {
    tokenDriveEsValido,
    guardarAccessToken,
    registrarRenovadorToken,
    descargarContenidoArchivo,
} from './drive.js';
import { abrirVentanaOAuthDrive } from './oauth-drive.js';

// ============================================================
// Reautorización de Drive (silenciosa y con popup)
//
// Reimplementada aquí en lugar de importar src/auth.js: ese módulo
// arranca la app completa (paneles admin, onboarding, listeners de
// login) al importarse, lo cual no corresponde a esta página ligera.
// Usa exactamente las mismas Cloud Functions y el mismo storage de
// token que el resto de Contest Manager.
// ============================================================

async function _renovarTokenSilencioso() {
    try {
        const driveRefreshFn = httpsCallable(functions, 'driveRefresh');
        const { data } = await driveRefreshFn({});
        if (!data?.access_token) return false;
        guardarAccessToken(data.access_token);
        return true;
    } catch {
        return false;
    }
}

// Permite que descargarContenidoArchivo() renueve el token sola ante un 401.
registrarRenovadorToken(_renovarTokenSilencioso);

async function _autorizarDriveConPopup() {
    const code = await abrirVentanaOAuthDrive(); // puede rechazar: popup-blocked/closed/cancelled
    const driveAuthorizeFn = httpsCallable(functions, 'driveAuthorize');
    const { data } = await driveAuthorizeFn({ code });
    if (!data?.access_token) {
        throw new Error('La función no devolvió access_token.');
    }
    guardarAccessToken(data.access_token);
    return true;
}

/**
 * Garantiza un access_token de Drive utilizable.
 * Orden: token vigente → renovación silenciosa → popup de consentimiento.
 * @returns {Promise<boolean>}
 */
export async function asegurarAccesoDrive() {
    if (tokenDriveEsValido()) return true;
    if (await _renovarTokenSilencioso()) return true;
    return _autorizarDriveConPopup();
}

// ============================================================
// Filtro de candidatos Cabrillo — solo metadatos, sin descargar
// nada. En Contest Manager tipo==='log' significa "archivo de
// contactos del concurso" en general (Cabrillo, ADIF, o lo que el
// usuario haya tenido a mano) — nunca significó específicamente
// "Cabrillo analizable". Esta es una heurística rápida para no
// ofrecer en el selector algo que obviamente no es Cabrillo; la
// validación definitiva sigue siendo el parser de Julio al pulsar
// Analizar (ver obtenerTextoLog / cabrillo-analizador.js).
// ============================================================

const EXTENSIONES_EXCLUIDAS = new Set([
    'pdf', 'adi', 'adif', 'jpg', 'jpeg', 'jfif', 'png', 'zip', 'xls', 'xlsx', 'doc', 'docx',
]);
const EXTENSIONES_INCLUIDAS = new Set(['log', 'cbr', 'txt']);

function extensionDe(nombre) {
    const m = /\.([a-z0-9]+)$/i.exec(String(nombre ?? '').trim());
    return m ? m[1].toLowerCase() : '';
}

function esCandidatoCabrillo(archivo) {
    const ext = extensionDe(archivo.nombre);
    if (ext && EXTENSIONES_EXCLUIDAS.has(ext)) return false;
    if (ext && EXTENSIONES_INCLUIDAS.has(ext)) return true;
    // Extensión desconocida o ausente: solo se acepta si el MIME es de texto.
    const mime = String(archivo.mimeType ?? '').toLowerCase();
    return mime.startsWith('text/');
}

// ============================================================
// Listado de concursos con log disponible
// ============================================================

/**
 * Concursos (editions) del usuario que tienen al menos un archivo
 * tipo 'log' que además sea un candidato Cabrillo plausible según
 * sus metadatos (ver esCandidatoCabrillo).
 * @param {string} uid
 * @returns {Promise<Array<{edicion: object, logFile: object}>>}
 */
export async function listarConcursosConLog(uid) {
    const consulta = query(collection(db, 'editions'), where('userId', '==', uid));
    const snapshot = await getDocs(consulta);

    const ediciones = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
        fechaInicio: docSnap.data().fechaInicio?.toDate?.() ?? null,
        fechaFin: docSnap.data().fechaFin?.toDate?.() ?? null,
    }));

    // Antes se consultaba la subcolección "files" de cada edición una por una
    // (await dentro de un for...of): N round-trips a Firestore EN SERIE, lo
    // que hacía que el selector demorara segundos con varios concursos.
    // Se disparan todas en paralelo: el tiempo total pasa a ser el de la
    // consulta más lenta, no la suma de todas.
    const logsPorEdicion = await Promise.all(
        ediciones.map((edicion) => obtenerArchivosEdicion(edicion.id, 'log'))
    );

    const resultado = [];

    ediciones.forEach((edicion, i) => {
        // Primero se filtran TODOS los archivos tipo 'log' de la edición por
        // candidatura Cabrillo, y recién entre esos se elige el más reciente
        // — así un PDF más nuevo no tapa un .log válido más viejo.
        const candidatos = (logsPorEdicion[i] || []).filter(esCandidatoCabrillo);
        if (!candidatos.length) return;

        const logFile = candidatos.reduce((masReciente, actual) => {
            const tActual = actual.subidoEn?.getTime?.() ?? 0;
            const tMasReciente = masReciente.subidoEn?.getTime?.() ?? 0;
            return tActual > tMasReciente ? actual : masReciente;
        }, candidatos[0]);

        resultado.push({ edicion, logFile });
    });

    resultado.sort((a, b) => {
        const fa = a.edicion.fechaInicio?.getTime?.() ?? 0;
        const fb = b.edicion.fechaInicio?.getTime?.() ?? 0;
        return fb - fa;
    });

    return resultado;
}

/**
 * Etiqueta legible para mostrar en el selector.
 * `indicativo` puede no estar presente en documentos existentes
 * (no todos los flujos de guardado lo persisten hoy); se omite
 * si falta en lugar de asumir su presencia.
 */
export function etiquetaConcurso(edicion) {
    const partes = [edicion.concurso || 'Concurso', edicion.año].filter(Boolean);
    let etiqueta = partes.join(' ');
    if (edicion.modo) etiqueta += ` — ${edicion.modo}`;
    if (edicion.indicativo) etiqueta += ` (${edicion.indicativo})`;
    return etiqueta;
}

// ============================================================
// Contenido del log (dispatch por storageType)
// ============================================================

function decodificarCabrillo(bytes) {
    return new TextDecoder('windows-1252').decode(bytes);
}

/**
 * Recupera el texto del Cabrillo, sin duplicarlo en ningún
 * almacenamiento intermedio: se resuelve directo desde el Drive
 * (propio o administrado) donde ya vive.
 * @param {object} logFile - doc de editions/{id}/files/{id}, tipo 'log'
 * @returns {Promise<string>}
 */
export async function obtenerTextoLog(logFile) {
    if (logFile.storageType === 'user_drive') {
        const autorizado = await asegurarAccesoDrive();
        if (!autorizado) {
            throw new Error('No se pudo autorizar el acceso a Google Drive.');
        }
        const buffer = await descargarContenidoArchivo(logFile.fileId);
        return decodificarCabrillo(new Uint8Array(buffer));
    }

    if (logFile.storageType === 'managed_drive') {
        const fn = httpsCallable(functions, 'managedDriveFileContent');
        const { data } = await fn({ fileId: logFile.fileId });
        if (!data?.ok || !data?.contentBase64) {
            throw new Error(data?.error || 'No se pudo recuperar el archivo.');
        }
        const binario = atob(data.contentBase64);
        const bytes = Uint8Array.from(binario, (c) => c.charCodeAt(0));
        return decodificarCabrillo(bytes);
    }

    throw new Error(`storageType desconocido: ${logFile.storageType}`);
}
